import { useEffect, useRef, useState } from 'react';
import { InspectionImageMeta, InspectionImageWithFile } from './types';
import { createMetaFromSource } from './utils';

interface InternalImageItem {
  id: string;
  previewUrl: string;
  file: File;
  meta: InspectionImageMeta;
  /**
   * Foto como veio da câmera/galeria, preservada em memória (at-24).
   *
   * A marcação de avaria é **queimada** na imagem (é assim que o traço chega ao PDF
   * e ao SGA, que baixa a foto da URL do GCS). Guardar o original permite: refazer a
   * marcação partindo da foto limpa — em vez de riscar por cima de traço — e remover
   * a avaria de verdade. Nada disso sobe ao servidor: só a imagem marcada é enviada.
   */
  fileOriginal: File;
  previewUrlOriginal: string;
}

interface UseInspectionCaptureArgs {
  captions: string[];
  maxPhotos: number;
  onImagesChange?: (images: InspectionImageMeta[]) => void;
  onImagesWithFileChange?: (images: InspectionImageWithFile[]) => void;
}

export const useInspectionCapture = ({ captions, maxPhotos, onImagesChange, onImagesWithFileChange }: UseInspectionCaptureArgs) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [images, setImages] = useState<InternalImageItem[]>([]);
  const imagesRef = useRef<InternalImageItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  /**
   * Índice do próximo SLOT VAZIO (at-24, bug B6).
   *
   * Antes era `images.length` — o que assume que as fotos preenchem as legendas em
   * sequência e nunca saem do meio. Ao excluir a foto 3 de 5, o índice virava 4 e as
   * legendas eram reatribuídas por posição, desalinhando tudo. Agora o próximo alvo é
   * a primeira legenda AINDA NÃO USADA: excluir libera exatamente aquele slot.
   */
  useEffect(() => {
    const legendasUsadas = new Set(images.map((img) => img.meta.caption));
    const primeiroVazio = captions.findIndex((c) => !legendasUsadas.has(c));

    setCurrentIndex(primeiroVazio === -1 ? captions.length : primeiroVazio);
  }, [images, captions]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  // Anexa o stream ao elemento de vídeo quando ambos existirem.
  // Isso evita o cenário comum no mobile: abrir a câmera antes do <video> estar montado,
  // o que resulta em preview/captura preta.
  useEffect(() => {
    const video = videoRef.current;
    if (!isCameraActive || !cameraStream || !video) return;

    let cancelled = false;

    const attachAndPlay = async () => {
      try {
        setIsCameraReady(false);
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        (video as any).autoplay = true;
        video.srcObject = cameraStream;

        // Esperas com timeout curto: não travar o app se algum evento não disparar.
        await Promise.race([
          new Promise<void>((resolve) => {
            if (video.readyState >= 1) return resolve();
            const onLoadedMetadata = () => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              resolve();
            };
            video.addEventListener('loadedmetadata', onLoadedMetadata);
          }),
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error('metadata-timeout')), 2500)),
        ]);

        await Promise.race([
          video.play(),
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error('play-timeout')), 2500)),
        ]);

        await Promise.race([
          new Promise<void>((resolve) => {
            if (video.readyState >= 2) return resolve();
            const onLoadedData = () => {
              video.removeEventListener('loadeddata', onLoadedData);
              resolve();
            };
            video.addEventListener('loadeddata', onLoadedData);
          }),
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error('data-timeout')), 2500)),
        ]);

        // Marca pronto quando já temos dimensões (ou após um pequeno grace period)
        const startedAt = Date.now();
        while (video.videoWidth === 0 || video.videoHeight === 0) {
          if (Date.now() - startedAt > 1500) break;
          await new Promise((r) => setTimeout(r, 80));
        }

        if (!cancelled) {
          setIsCameraReady(video.videoWidth > 0 && video.videoHeight > 0);
        }

        if (!cancelled) {
          setError(null);
        }
      } catch (e) {
        console.error('Erro ao iniciar pré-visualização da câmera:', e);
        if (!cancelled) {
          const err = e as Error;
          setError(`${err.name || 'Erro'}: ${err.message || 'Falha ao iniciar a câmera.'}`);
        }
      }
    };

    attachAndPlay();

    return () => {
      cancelled = true;
    };
  }, [cameraStream, isCameraActive]);

  useEffect(() => {
    if (onImagesChange) {
      onImagesChange(images.map((item) => item.meta));
    }

    if (onImagesWithFileChange) {
      onImagesWithFileChange(
        images.map((item) => ({
          id: item.id,
          previewUrl: item.previewUrl,
          file: item.file,
          meta: item.meta,
        }))
      );
    }
  }, [images, onImagesChange, onImagesWithFileChange]);

  const startCamera = async () => {
    setError(null);
    try {
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        setError('A câmera exige HTTPS no celular (use o túnel Cloudflare).');
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Seu navegador não suporta acesso à câmera (getUserMedia).');
        return;
      }

      // Fecha qualquer stream anterior antes de abrir outro
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      setIsCameraReady(false);

      // Para ficar mais rápido: tenta primeiro constraints simples e só aumenta complexidade se falhar.
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }

      streamRef.current = stream;

      // Ativa UI + publica stream para o effect anexar no <video>
      setIsCameraActive(true);
      setCameraStream(stream);
    } catch (e) {
      console.error('Erro ao acessar a câmera:', e);
      const err = e as Error;
      stopCamera();
      setError(`${err.name || 'Erro'}: ${err.message || 'Não foi possível acessar a câmera.'}`);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraStream(null);
    setIsCameraActive(false);
  };

  const captureFromCamera = async () => {
    if (!videoRef.current || !isCameraActive) return;
    if (currentIndex >= maxPhotos) return;

    const video = videoRef.current;
    const stream = streamRef.current;

    try {
      setError(null);

      if (!stream || !video.srcObject) {
        setError('Câmera não está ativa. Clique em “Abrir câmera” novamente.');
        return;
      }

      // Garante que temos frame disponível
      if (video.readyState < 2) {
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => resolve(), 300);
          const onLoadedData = () => {
            clearTimeout(timeout);
            video.removeEventListener('loadeddata', onLoadedData);
            resolve();
          };
          video.addEventListener('loadeddata', onLoadedData);
        });
      }

      // Aguarda pelo menos um frame (evita captura preta em alguns devices)
      if (typeof (video as any).requestVideoFrameCallback === 'function') {
        await new Promise<void>((resolve) => {
          (video as any).requestVideoFrameCallback(() => resolve());
        });
      } else {
        await new Promise((resolve) => setTimeout(resolve, 120));
      }

      let width = video.videoWidth;
      let height = video.videoHeight;

      if ((!width || !height) && stream) {
        const track = stream.getVideoTracks?.()[0];
        const settings = track?.getSettings?.();
        width = (settings?.width as number) || 1280;
        height = (settings?.height as number) || 720;
      }

      if (!width || !height) {
        width = 1280;
        height = 720;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      if (typeof (window as any).createImageBitmap === 'function') {
        try {
          const bitmap = await (window as any).createImageBitmap(video);
          ctx.drawImage(bitmap, 0, 0, width, height);
          if (typeof (bitmap as any).close === 'function') {
            (bitmap as any).close();
          }
        } catch {
          ctx.drawImage(video, 0, 0, width, height);
        }
      } else {
        ctx.drawImage(video, 0, 0, width, height);
      }

    // Legenda sobre a imagem
    const caption = captions[currentIndex] || `Foto ${currentIndex + 1}`;
    const padding = Math.max(12, Math.round(canvas.width * 0.015));
    const fontSize = Math.max(16, Math.round(canvas.width * 0.025));
    const rectHeight = fontSize + padding * 2;
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, canvas.height - rectHeight, canvas.width, rectHeight);
    ctx.fillStyle = '#fff';
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 4;
    ctx.fillText(caption, padding, canvas.height - padding);
    ctx.shadowBlur = 0;

      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9)
      );

      if (!blob) {
        setError('Falha ao gerar a imagem (blob vazio).');
        return;
      }

      const file = new File([blob], `vistoria-camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const previewUrl = dataUrl;
      const id = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      const meta = await createMetaFromSource(id, caption, 'camera');

      setImages((prev) => [
        ...prev,
        // fileOriginal/previewUrlOriginal: a foto limpa fica em memória para
        // permitir refazer/remover a marcação de avaria (at-24).
        { id, previewUrl, file, meta, fileOriginal: file, previewUrlOriginal: previewUrl },
      ]);
      setCurrentIndex((prev) => {
        const next = prev + 1;
        if (next >= maxPhotos) {
          stopCamera();
        }
        return next;
      });
    } catch (e) {
      console.error('Erro ao tirar foto:', e);
      const err = e as Error;
      setError(`${err.name || 'Erro'}: ${err.message || 'Falha ao capturar a foto.'}`);
    }
  };

  const handleFileInput = async (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);

    // Slots ainda vazios, na ordem — o seletor de arquivos preenche um por vez.
    // Não usa `currentIndex + n` porque um slot no meio pode ter sido liberado.
    const usadas = new Set(imagesRef.current.map((img) => img.meta.caption));
    const vazios = captions.filter((c) => !usadas.has(c));
    let alvo = 0;

    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) continue;
      if (alvo >= vazios.length) break;

      const previewUrl = URL.createObjectURL(file);
      const id = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      const caption = vazios[alvo];
      const meta = await createMetaFromSource(id, caption, 'gallery');

      setImages((prev) => [
        ...prev,
        // fileOriginal/previewUrlOriginal: a foto limpa fica em memória para
        // permitir refazer/remover a marcação de avaria (at-24).
        { id, previewUrl, file, meta, fileOriginal: file, previewUrlOriginal: previewUrl },
      ]);
      alvo += 1;

      // O effect de slots recalcula o `currentIndex`; aqui só fecha a câmera quando
      // não há mais slot a preencher.
      if (alvo >= vazios.length) {
        stopCamera();
      }
    }
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const toRemove = prev.find((img) => img.id === id);
      if (toRemove?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(toRemove.previewUrl);
      }

      // NÃO renormaliza as legendas (at-24, bug B6): a legenda é do SLOT e está
      // QUEIMADA na imagem pela captura. Reatribuir por posição fazia toda foto
      // seguinte à removida ficar com o rótulo de outra — a foto mostrava um ângulo
      // e o metadado (que vai ao laudo e ao SGA) dizia outro. Remover libera o slot
      // daquela legenda, e a próxima captura preenche justamente esse slot.
      return prev.filter((img) => img.id !== id);
    });
  };

  /**
   * Aplica a marcação de avaria: substitui a foto pela versão com o traço queimado
   * (at-24). O `fileOriginal` é preservado, então "Editar" volta a partir da foto
   * limpa e "Remover avaria" desfaz de verdade.
   *
   * Só a imagem marcada é enviada ao servidor — é ela que o PDF do laudo destaca e
   * que o SGA baixa do GCS.
   */
  const aplicarMarcacaoAvaria = (id: string, arquivoMarcado: File) => {
    const previewUrlMarcado = URL.createObjectURL(arquivoMarcado);

    setImages((prev) =>
      prev.map((img) => {
        if (img.id !== id) return img;

        // Revoga o preview anterior SÓ se ele não for o original (que segue em uso
        // como base para reedição) — do contrário a foto limpa ficaria inacessível.
        if (
          img.previewUrl !== img.previewUrlOriginal &&
          img.previewUrl.startsWith('blob:')
        ) {
          URL.revokeObjectURL(img.previewUrl);
        }

        return {
          ...img,
          file: arquivoMarcado,
          previewUrl: previewUrlMarcado,
          meta: { ...img.meta, damaged: true },
        };
      })
    );
  };

  /** Volta a foto ao original e limpa a flag de avaria. */
  const removerMarcacaoAvaria = (id: string) => {
    setImages((prev) =>
      prev.map((img) => {
        if (img.id !== id) return img;

        if (
          img.previewUrl !== img.previewUrlOriginal &&
          img.previewUrl.startsWith('blob:')
        ) {
          URL.revokeObjectURL(img.previewUrl);
        }

        return {
          ...img,
          file: img.fileOriginal,
          previewUrl: img.previewUrlOriginal,
          meta: { ...img.meta, damaged: false },
        };
      })
    );
  };

  return {
    videoRef,
    images,
    currentIndex,
    isCameraActive,
    isCameraReady,
    error,
    startCamera,
    stopCamera,
    captureFromCamera,
    handleFileInput,
    removeImage,
    aplicarMarcacaoAvaria,
    removerMarcacaoAvaria,
  };
};
